// =============================================================================
// useScrolled Hook
// =============================================================================
// Detects whether the user has scrolled past a certain point on the page.
// Used by the Navbar to switch from transparent (over the hero image)
// to a solid background color.
//
// HOW IT WORKS:
// 1. Listens to the browser's "scroll" event
// 2. If scrollY (pixels scrolled from the top) > threshold, returns true
// 3. The Navbar uses this to toggle a CSS class: "is-scrolled"
//
// PERFORMANCE:
// - Uses { passive: true } on the scroll listener — this tells the browser
//   "we won't call preventDefault(), so you can optimize scrolling"
// - The threshold defaults to 64px (roughly the height of the hero overlap)
// =============================================================================

import { useState, useEffect } from 'react';

/**
 * Returns true when the page has been scrolled past the given threshold.
 *
 * @param threshold - Number of pixels from the top before triggering (default: 64)
 * @returns boolean - true if scrolled past threshold, false otherwise
 *
 * USAGE:
 *   const isScrolled = useScrolled(100); // true when scrolled past 100px
 *   <nav className={isScrolled ? 'navbar is-scrolled' : 'navbar'}>
 */
export function useScrolled(threshold: number = 64): boolean {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    // Handler that checks scroll position
    const handleScroll = () => {
      setIsScrolled(window.scrollY > threshold);
    };

    // Check immediately in case the page loads already scrolled
    // (e.g., user refreshed while scrolled down)
    handleScroll();

    // Listen for scroll events with passive: true for performance
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Cleanup: remove the listener when the component unmounts
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [threshold]);

  return isScrolled;
}
